import Empty from '@/assets/placeholders/empty.png';

import { CoinChartColorType, WhiteLabelTheme } from '../types';

import BannerImage from './assets/banner.svg';
import CardLogo from './assets/card-logo.svg';
import SettingsBackgroundIllustration from './assets/ill_settings_decoration.svg';
import LoginBackground from './assets/login-background.svg';
import LogoIcon from './assets/logo.svg';
import manageProfileBanner from './assets/manageProfileBanner.svg';
import SmallLogoIcon from './assets/minify-logo.svg';
import QrCodeLogo from './assets/qrCodeLogo.svg';
import ReimburseMeSidebar from './assets/reimburseMeSidebar.svg';
import SidebarLogoIcon from './assets/sidebar-logo.svg';
import SidebarSmallLogoIcon from './assets/sidebar-minify-logo.svg';
import SignInLogo from './assets/signIn-logo.svg';
import VerificationBanner from './assets/verificationBackground.svg';

const buttonColors = {
  onDarkButtonPrimary: '#18ABC4',
  onDarkButtonPrimaryHover: '#0F92A8',
  onDarkButtonPrimaryActive: '#0C7586',
  onDarkButtonPrimaryPressed: '#0C7586',
  onDarkButtonPrimaryDisabled: '#09505C',

  onDarkButtonSecondaryHoverBorder: '#15ADC7',
  onDarkButtonSecondaryActiveBorder: '#0C7586',
  onDarkButtonSecondaryDisabledBorder: '#09505C',
  onDarkButtonSecondaryDisabled: '#0A0046',
  onDarkButtonSecondaryHover: '#0F407C',

  onDarkModuleBackground: '#1A1053',
  onDarkBackground: '#0A0046',

  onDarkIconDisabled: '#747474',
  onDarkText: '#747474',

  buttonPrimary: '#00B4D2',
  buttonPrimaryHover: '#009CB6',
  buttonPrimaryActive: '#147787',
  buttonPrimaryPressed: '#009CB6',
  buttonPrimaryDisabled: '#EAECF9',

  buttonSecondaryHoverBorder: '#00B4D2',
  buttonSecondaryActiveBorder: '#147787',
  buttonSecondaryDisabledBorder: '#EAECF9',
  buttonSecondaryDisabled: '#FFFFFF',
  buttonSecondaryHover: '#EAECF9',

  buttonTertiaryHover: '#EDF0FF',
  buttonTertiaryPressed: '#DCE8FC',
  buttonTertiaryDisabled: '#E8E9EF',
  buttonSecondaryContainer: '#EAECF9',
};
const navigationColors = {
  navigationBackground: '#0A0046',
  navigationBorder: '#241867',
  navigationAccent: '#00B4D2',
  navigationAccent2: '#00B4D2',
  navigationTextDefault: '#FFFFFF',
  navigationTextAccent: '#FFFFFF',
  navigationIconDefault: '#FFFFFF',
  navigationIconAccent: '#FFFFFF',
  navigationAccentHover: '#096575',
  navigationAccentActive: '#1B8799',
};
const backgroundColors = {
  background1: '#0A0046',
  background2: '#0A0046',
  background3: '#000000',

  border: '#BABABC',
  border1: '#DCDCDC',
  border2: '#EAEAEA',
  module: '#FBFAFA',
  canvas: '#FFFFFF',
  stressContainer: '#4F5557',
  accentActive: '#00B4D2',
  accentBorder: '#BABABC',
  accentContainer: '#F5F7FF',

  popupBg2: '#0F16210D',
  legend: '#C2D6E3',
};

const baseColors = {
  ...buttonColors,
  ...navigationColors,
  ...backgroundColors,
  textTitle: '#0F1621',
  textBody: '#0F1621',
  textActive: '#2264DC',
  textActiveHover: '#1A53BD',

  ancor: '#2264DC',
  ancorHover: '#1A53BD',
  activeButtonText: '#2264DC',
  paginationActiveItem: '#00B4D2',
  buttonText: '#FFFFFF',
  sidebarDelimiter: '#BFBFBF',

  tableHeader: '#0F1621',

  textSecondary: '#6B7485',
  textDisabled: '#9BA3B3',
  textOnColor: '#FFFFFF',
  textAccent: '#2264DC',
  textAccentHover: '#1A53BD',
  textAccentActive: '#19438F',

  iconPrimary: '#0F1621',
  iconActive: '#0F1621',
  iconActiveHover: '#0F1621',

  iconSecondary: '#6B7485',
  iconOnColor: '#FFFFFF',
  iconAccent: '#2264DC',
  iconAccentHover: '#1A53BD',
  iconAccentActive: '#19438F',
  iconDisabled: '#9BA3B3',

  success: '#19B269',
  successBorder: '#B8E7D1',
  successContainer: '#E8F7F0',

  warning: '#F9C134',
  warningBorder: '#FDECC0',
  warningContainer: '#FEF9EB',

  danger: '#E72626',
  dangerBorder: '#F8BCBC',
  dangerContainer: '#FDE9E9',
  dangerHover: '#A61C18',
  dangerActive: '#66120A',

  info: '#176AF6',
  infoBorder: '#B7D1FC',
  infoContainer: '#E8F0FE',

  transactionDental: '#176AF6',
  transactionVision: '#481A54',
  transactionMedical: '#00C3B3',
  transactionOther: '#FF9D0A',
  transactionParking: '#323D9B',
  transactionTransit: '#9BBF84',
  transactionAdoption: '#0F7970',
  transactionDependentCare: '#CC3381',
  transactionPharmacy: '#D83B00',
  transactionLifestyle: '#D3375C',
  transactionPremium: '#3628D1',
  transactionTravel: '#F26B31',
  transactionWellness: '#9B31F2',
  transactionPlanSpecific: '#00B5EE',
  refund: '#588C0E',
  transactionDefault: '#5E697E',
  claimTransferInternal: '#E5E9F0',
  nonClaimIcon: '#151F6D',
  nonClaimBg: '#E5E9F0',

  planTypeHSA: '#2EA12B',
  planTypeHCFSA: '#FA802A',
  planTypeDCAP: '#CC3381',
  planTypeHRA: '#134E8F',
  planTypeCommuterTransit: '#9BBF84',
  planTypeCommuterParking: '#323D9B',
  planTypeAdoption: '#0F7970',
  planTypeLifestyle: '#D3375C',
  planTypeSpecialty: '#00B5EE',
  planTypeTravel: '#F26B31',
  planTypeRx: '#7909AD',
  planTypeDental: '#176AF6',
  planTypeVision: '#481A54',
  planTypeMedical: '#00C3B3',

  lockedNull: '#000000',
  processingPending: '#F9C134',
  needsHelp: '#FF9D0A',
  paidActiveComplete: '#19B269',
  partiallyPaid: '#B9CC29',
  // waitingForFunds
  waitingForFunds: '#8EB2A1',
  //
  deniedErrorExpired: '#E72626',
  uploading: '#9BA3B3',
  uploadedApproved: '#E8F0FE',
  loa: '#00B5EE',
  retirement: '#CE8258',
  future: '#7909AD',

  missingDetail: '#FB6D2A',
  missingContainer: '#FFF3EE',
  missingBorder: '#FEDACA',

  investmentsfuture: '#7909AD',
  investmentsFund1: '#3F68FF',
  investmentsFund2: '#10D8B9',
  investmentsFund3: '#FB5C20',
  investmentsFund4: '#FC990F',
  investmentsFund5: '#FDD137',
  investmentsFund6: '#BCD1EF',
  investmentsFund7: '#323D9B',
  investmentsFund8: '#F3C626',
  investmentsFund9: '#A528C6',
  investmentsFund10: '#0C95D0',
  investmentsFund11: '#619C00',
  investmentsFund12: '#CC3381',
  investmentBackground: '#0A0046',
  profitContainer: '#00FF9314',
  lossContainer: '#EF235D14',
  chart: '#18ABC4',
  coinChart: ['#B8E7D1', '#E8F7F0', '#C3F5DD', '#E8F7F0', '#E8F7F0'] as CoinChartColorType,

  // new
  partner: '#D83B00',
  distributor: '#904D27',
  employer: '#174FBC',
  subsidiary: '#0F7970',
  subgroup: '#481A54',
  claimProcessor: '#176AF6',
  employee: '#0CAD09',
  dependent: '#5E1696',
  noAssignee: '#B8BFCC',

  claimParticipant: '#CC3381',
  claimSystem: '#18A0FB',
  claimCardProcessor: '#19B269',

  uploadLinkBackground: '#00B4D2',
};
export default {
  title: 'Helix',
  loginIconWidth: 315,
  loginIconHeight: 120,

  IOSAppStoreLink: 'https://apps.apple.com/us/app/helix-benefits/id6754824621',
  androidAppStoreLink: 'https://play.google.com/store/apps/details?id=net.helixbenefits.app',
  mobileAppIcon: SmallLogoIcon,
  mobileAppTitle: 'Helix Benefits',

  loginScreenTitle:
    'The only automated, AI-powered, and ultra-reliable solution for today’s consumer benefits.',
  colors: {
    ...baseColors,
    loginTitleColor: '#0F1621',
    //
    popup: 'rgba(0, 0, 0, 0.5)',
    popup2: 'rgba(15, 22, 33, 0.05)',
    fade1: 'linear-gradient(360deg, #F7F3F1 0%, rgba(255, 255, 255, 0) 100%)',
    fade2: 'linear-gradient(180deg, #FAFAFA 0%, #F4F4F4 100%)',
  },
  logo: LogoIcon,
  minifyLogo: SmallLogoIcon,
  font: "'Montserrat', sans-serif",
  titleFont: "'Montserrat', sans-serif",
  tetriaryAccentButton: true,
  images: {
    settingsIllustration: SettingsBackgroundIllustration,
    banner: BannerImage,
    cardBackground: Empty,
    loginBackground: LoginBackground,
    cardLogo: CardLogo,
    sidebarLogo: SidebarLogoIcon,
    sidebarMinifyLogo: SidebarSmallLogoIcon,
    qrCodeLogo: QrCodeLogo,
    manageProfileBannerBackground: manageProfileBanner,
    investmentsBannerRight: ' ',
    reimburseMeSidebar: ReimburseMeSidebar,
    signUpSidebar: ReimburseMeSidebar,
    verificationBackground: VerificationBanner,
    signInLogo: SignInLogo,
  },
  fonts: {
    sidebarWeight: 500,
    sidebarFontSize: '14px',
    reimbursementWeight: 500,
    sizes: {
      small: {
        size: '12px',
        height: '16px',
        maxWidth: '336px',
      },
      '3xl': {
        size: '30px',
        height: '40px',
        maxWidth: '1008px',
      },
      '2xl': {
        size: '24px',
        height: '30px',
        maxWidth: '816px',
      },
    },
  },
  rounds: {
    largeRound: '0px',
    moduleRound: '12px',
    container1Round: '8px',
    container2Round: '4px',
    checkboxRound: '4px',
    navigationButtonRound: '4px',
    fieldRound: '4px',
    button1Round: '4px',
    button2Round: '2px',
    dropdownItemRound: '2px',
    snackbarRound: '2px',
    smallRound: '0px',
  },

  shadows: {
    default: '0px 1px 7px rgba(0, 0, 0, 0.04);',
    hover: '0px 3px 10px rgba(0, 0, 0, 0.1);',
    active: '0px 8px 25px rgba(15, 116, 116, 0.25);',

    highlighted: '0px 6px 16px rgba(10, 128, 128, 0.5);',
  },
  favicon: '/helix/favicon.ico',
  favicon16: '/helix/favicon-16x16.png',
  favicon32: '/helix/favicon-32x32.png',
  faviconAppleTouch: '/helix/apple-touch-icon-fidelity.png',
} as WhiteLabelTheme;
